import styled from "@emotion/styled";
import { Stack, type TextFieldProps, TextareaAutosize, Typography } from "@mui/material";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { type FC, type JSX, useState } from "react";
import { type Control, Controller, type FieldErrors, type FieldPath, type FieldValues } from "react-hook-form";
import { StyledFormLabelWrapper } from "./FormStyles";

type Props<FormType extends FieldValues> = Omit<TextFieldProps, "onChange"> & {
  readonly name: FieldPath<FormType>;
  readonly label?: string;
  readonly control: Control<FormType, object>;
  readonly errors: FieldErrors<FormType>;
  readonly disabled?: boolean;
  labelEndAdornment?: JSX.Element;
};

const StyledInputWrapper = styled.div`
  display: flex;
  flex-flow: column;

  .input-wrapper {
    position: relative;
  }
`;

const StyledTextarea = styled(TextareaAutosize)(
  ({ theme }) => `
  outline: 0;
  box-sizing: border-box;
  width: 100%;
  font-family: Roboto;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.3;
  padding: ${theme.spacing(Spacing.L)};
  border-radius: ${theme.spacing(Radius.M)};
  color: ${theme.colors.textPrimary};
  border-color: ${theme.colors.border};
  resize: vertical;
`,
);

export const FormTextarea = <T extends object>({ control, name, label, labelEndAdornment, placeholder, disabled }: Props<T>) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <StyledInputWrapper>
            {!!label && (
              <StyledFormLabelWrapper htmlFor={name}>
                <Stack direction={"row"} gap={1}>
                  <Typography fontWeight={500} variant="body2" mb={Spacing.S}>
                    {label}
                  </Typography>
                  {labelEndAdornment}
                </Stack>
              </StyledFormLabelWrapper>
            )}
            <StyledTextarea
              placeholder={placeholder}
              disabled={disabled}
              value={value}
              minRows={6}
              onChange={(event) => {
                onChange(event.target.value);
              }}
            />
          </StyledInputWrapper>
        );
      }}
    />
  );
};
